import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { exportPlanPdf } from "@/lib/pdf";
import { Download, Save, Check, User, Users, FileText, Landmark, Laptop, ShieldCheck, Flower, Heart, Coins, Zap, Lock } from "lucide-react";
import PaywallCard from "@/components/PaywallCard";
import PrivacyNotice from "@/components/PrivacyNotice";

const LETTER_PROMPTS = [
  {
    name: "how_much_you_mean",
    label: "\u201cWhat I want you to know about how much you mean to me\u2026\u201d",
    helper: "Start by finishing that sentence and let yourself describe specific moments, qualities, or habits you love about them.",
  },
  {
    name: "favourite_memory",
    label: "\u201cMy favourite memory with you is\u2026\u201d",
    helper: "Choose one memory (it doesn't have to be huge) and describe where you were, what you were doing, and why it stays with you.",
  },
  {
    name: "grateful_never_said",
    label: "\u201cSomething I\u2019m grateful to you for, that I may never have said out loud, is\u2026\u201d",
    helper: "Think of a time they supported you, believed in you, or simply made you feel safe.",
  },
  {
    name: "remember_this_about_you",
    label: "\u201cIf you ever doubt yourself, I want you to remember this about you\u2026\u201d",
    helper: "Write down the strengths you see in them: kindness, resilience, humour, loyalty, creativity, etc.",
  },
  {
    name: "changed_my_life",
    label: "\u201cOne way you\u2019ve changed my life for the better is\u2026\u201d",
    helper: "Explain how knowing them has shifted your path, your thinking, or your sense of who you are.",
  },
  {
    name: "three_wishes",
    label: "\u201cHere are three wishes I have for your future\u2026\u201d",
    helper: "These can be emotional (peace, joy, self-trust) as well as practical (travel, love, meaningful work).",
  },
  {
    name: "hard_day_words",
    label: "\u201cIf I could be right next to you on a hard day, this is what I\u2019d say\u2026\u201d",
    helper: "Imagine they\u2019re going through something tough and write the comfort, encouragement, or guidance you\u2019d want them to hear.",
  },
  {
    name: "how_i_hope_youll_remember_me",
    label: "\u201cWhen you think of me, I hope you remember\u2026\u201d",
    helper: "Share the way you\u2019d like to be remembered: perhaps your values, the way you loved them, or simply your shared jokes and rituals.",
  },
];
export { LETTER_PROMPTS };

const SECTIONS = [
  {
    key: "personal",
    icon: <User size={18} strokeWidth={1.5} />,
    title: "Personal details",
    intro: "The basics that identify you and where your records live.",
    fields: [
      { name: "full_name", label: "Full legal name", type: "text" },
      { name: "date_of_birth", label: "Date of birth (DD/MM/YYYY)", type: "text" },
      { name: "national_insurance", label: "National Insurance number", type: "text" },
      { name: "address", label: "Home address", type: "textarea" },
      { name: "gp_practice", label: "GP practice", type: "text" },
    ],
  },
  {
    key: "executors",
    icon: <Users size={18} strokeWidth={1.5} />,
    title: "Executors & key contacts",
    intro: "The people who will act on your behalf. Ideally, tell them.",
    fields: [
      { name: "executor_1", label: "Executor 1 — name, relationship, phone", type: "textarea" },
      { name: "executor_2", label: "Executor 2 — name, relationship, phone", type: "textarea" },
      { name: "solicitor", label: "Solicitor — firm, contact, address", type: "textarea" },
      { name: "accountant", label: "Accountant — name and contact", type: "textarea" },
      { name: "attorney_lpa", label: "Solicitor under LPA (if different)", type: "textarea" },
      { name: "workplace_contact", label: "My workplace point of contact (Manager / HR)", type: "textarea" },
    ],
  },
  {
    key: "will",
    icon: <FileText size={18} strokeWidth={1.5} />,
    title: "Will & legal documents",
    intro: "Where the paperwork lives. Not the contents — just the location.",
    fields: [
      { name: "will_location", label: "Where the original will is stored", type: "textarea" },
      { name: "will_date", label: "Date will was last updated", type: "text" },
      { name: "lpa_status", label: "LPA status (registered? which types?)", type: "textarea" },
      { name: "other_documents", label: "Other important documents (passport, deeds, birth cert)", type: "textarea" },
    ],
  },
  {
    key: "accounts",
    icon: <Landmark size={18} strokeWidth={1.5} />,
    title: "Bank accounts & finances",
    intro: "List banks, not account numbers, for safety. Your executor can find the rest.",
    sensitive: true,
    fields: [
      { name: "banks", label: "Bank(s) & building societies you use", type: "textarea" },
      { name: "credit_cards", label: "Credit cards", type: "textarea" },
      { name: "investments", label: "Investments / ISAs / shares — provider names", type: "textarea" },
      { name: "pensions", label: "Pensions — workplace + personal + State", type: "textarea" },
      { name: "mortgage", label: "Mortgage provider & rough remaining", type: "textarea" },
      { name: "debts", label: "Loans or other debts", type: "textarea" },
    ],
  },
  {
    key: "digital",
    icon: <Laptop size={18} strokeWidth={1.5} />,
    title: "Digital accounts",
    intro: "The modern paperwork. A password manager is your best friend here.",
    sensitive: true,
    fields: [
      { name: "email_accounts", label: "Primary email account(s)", type: "text" },
      { name: "password_manager", label: "Password manager (e.g. 1Password, Bitwarden) & where the master password is kept", type: "textarea" },
      { name: "social_media", label: "Social media accounts (Facebook, Instagram, X)", type: "textarea" },
      { name: "cloud_storage", label: "Cloud storage / photo backups (iCloud, Google, Dropbox)", type: "textarea" },
      { name: "subscriptions", label: "Recurring media / service subscriptions to cancel (Netflix, Spotify, gym, streaming, magazines — utilities go in the next section)", type: "textarea" },
    ],
  },
  {
    key: "utilities",
    icon: <Zap size={18} strokeWidth={1.5} />,
    title: "Utilities",
    intro: "The everyday accounts your executor will need to close, transfer or pause. Provider name plus account reference is usually enough — you don't need to include passwords.",
    fields: [
      { name: "gas_electricity", label: "Gas & Electricity (supplier, account number, meter type if known)", type: "textarea" },
      { name: "water", label: "Water (supplier & account number)", type: "text" },
      { name: "mobile", label: "Mobile provider (network & mobile number)", type: "text" },
      { name: "broadband", label: "Broadband provider (supplier & account number)", type: "text" },
      { name: "other", label: "Other (council tax reference, TV licence, home phone, boiler cover, anything else)", type: "textarea" },
    ],
  },
  {
    key: "insurance",
    icon: <ShieldCheck size={18} strokeWidth={1.5} />,
    title: "Insurance",
    intro: "Policies your family may need to claim on.",
    fields: [
      { name: "life_insurance", label: "Life insurance — provider(s) & policy number(s)", type: "textarea" },
      { name: "home_insurance", label: "Home & contents insurance", type: "text" },
      { name: "health_insurance", label: "Private health insurance (if any)", type: "text" },
      { name: "funeral_plan", label: "Pre-paid funeral plan (provider & reference)", type: "textarea" },
    ],
  },
  {
    key: "other_assets",
    icon: <Coins size={18} strokeWidth={1.5} />,
    title: "Other Assets",
    intro: "Anything of value that doesn't sit in a UK bank account — shares, crypto, land, art, collectibles. Note where things are, not usually the credentials themselves.",
    fields: [
      { name: "shares_broker", label: "Shares — broker or platform (e.g. Hargreaves Lansdown, AJ Bell, Interactive Investor)", type: "textarea" },
      { name: "shares_holdings", label: "Rough summary of holdings (companies, funds, approximate value)", type: "textarea" },
      { name: "shares_certificates", label: "Where any paper share certificates are kept", type: "text" },
      { name: "shares_options_rsus", label: "Employer stock options, RSUs, EMI, SAYE — plan admin & vesting notes", type: "textarea" },

      { name: "crypto_exchanges", label: "Crypto — exchanges used (Coinbase, Kraken, Binance, etc.)", type: "textarea" },
      { name: "crypto_wallets", label: "Self-custody wallets (hardware/software) — brand, model, where it lives", type: "textarea" },
      { name: "crypto_recovery_location", label: "Where the recovery phrase / seed words are stored (do NOT write the phrase here)", type: "textarea" },
      { name: "crypto_summary", label: "Approximate holdings & purpose (long-term, staked, NFTs, etc.)", type: "textarea" },

      { name: "land_properties", label: "Land or property not already covered — address, ownership share, mortgage-free?", type: "textarea" },
      { name: "land_deeds", label: "Where the title deeds / Land Registry references are held", type: "textarea" },
      { name: "land_foreign", label: "Any land or property held overseas (country, address, local advisor)", type: "textarea" },

      { name: "other_valuables", label: "Other — art, jewellery, watches, cars, collectibles (with location and any valuations)", type: "textarea" },
      { name: "other_business_ip", label: "Business ownership, partnerships, intellectual property, royalties", type: "textarea" },
      { name: "other_safety_deposit", label: "Safety deposit boxes / private vaults — provider, box number, key location", type: "textarea" },
      { name: "other_notes", label: "Anything else you'd like your executor to know", type: "textarea" },
    ],
  },
  {
    key: "wishes",
    icon: <Flower size={18} strokeWidth={1.5} />,
    title: "Funeral wishes",
    intro: "A gentle kindness to those you love. Answer as much or as little as feels right — a general tone and a couple of key wishes is often enough. See the Funeral wishes guide chapter for fuller prompts and examples.",
    fields: [
      {
        name: "burial_or_cremation",
        label: "1. Burial, cremation, or something else",
        helper: "Would you prefer burial or cremation? If burial — a particular cemetery, family plot, or existing paperwork? If cremation — full service or a simple/direct cremation? Any wishes for your ashes (scattered, buried, kept at home, made into something meaningful)? It's fine to write 'I'm not sure yet, but I'm leaning towards…'.",
        type: "textarea", rows: 5,
      },
      {
        name: "ceremony",
        label: "2. The feel of the ceremony",
        helper: "Religious (which faith or tradition?), humanist / non-religious, spiritual but not tied to a faith, or something else? A particular place — church, crematorium chapel, outdoors, community hall, favourite venue? How should the atmosphere feel — quiet and reflective, gentle and hopeful, informal, a celebration of life?",
        type: "textarea", rows: 5,
      },
      {
        name: "speakers",
        label: "3. People you'd like to be involved",
        helper: "Anyone you'd especially like to lead the ceremony (celebrant, minister, priest, imam, rabbi, humanist celebrant)? Anyone to give a eulogy or read a poem/prayer? People you definitely do — or don't — want involved? Any pallbearers you'd like to nominate, or would you prefer professionals?",
        type: "textarea", rows: 5,
      },
      {
        name: "music_readings",
        label: "4. Music, readings, and personal touches",
        helper: "Songs, hymns, classical pieces or playlists that mean something to you. Live music, recorded, or none? Any poems, readings, prayers or quotes (note where they're from and where to find them)? Photos you'd like used. Stories you hope will be shared. One song and one reading is absolutely enough.",
        type: "textarea", rows: 5,
      },
      {
        name: "flowers_donations",
        label: "5. Flowers, donations, and dress code",
        helper: "Flowers — yes, no, or specific types/colours? Any that feel very 'you', or any you'd rather not have? A charity or cause you'd like people to donate to instead of (or as well as) flowers? Dress code — traditional dark, bright colours, 'no black', team shirts, cultural or faith attire?",
        type: "textarea", rows: 5,
      },
      {
        name: "after_ceremony",
        label: "6. After the ceremony",
        helper: "Would you like a wake or gathering afterwards? Where — a particular venue, pub, home, community space? Any food or drink you'd particularly like (tea and cake, a proper curry, a toast with…)? Any acts of remembrance — planting a tree, lighting candles, a memory book, sharing photos or stories? Or would you prefer no formal gathering at all?",
        type: "textarea", rows: 5,
      },
      {
        name: "other_wishes",
        label: "7. Any other wishes",
        helper: "What you'd like to be dressed in (favourite outfit, cultural dress, something symbolic). Objects to be placed with you (photos, letters, small items — some crematoria have restrictions). Specific cultural, community or faith-based rituals. Any 'last notes' for your loved ones. Also anything you don't want — for example 'no formal obituary' or 'please avoid anything very sombre'.",
        type: "textarea", rows: 5,
      },
    ],
  },
  {
    key: "letters",
    icon: <Heart size={18} strokeWidth={1.5} />,
    title: "Letters to loved ones",
    intro: "You can pick just one prompt per letter, or combine a few; even a short paragraph is enough, and it doesn't need to sound \u201cperfect\u201d or poetic — only honest and loving. When you export your plan as a PDF, the prompts are hidden — only what you've written appears, so each letter reads as your own words and sits on its own page.",
    outro: "Before you seal your letter, take a few minutes to choose one or two favourite photographs of your loved one to include, so that the person receiving it has something tangible and comforting to hold onto alongside your words.",
    layout: "letters-tabs",
    sensitive: true,
    fields: [],  // rendered by LettersTabs
  },
];

export default function Plan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState({ sections: {} });
  const [active, setActive] = useState("personal");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/plan").then((r) => setPlan(r.data || { sections: {} })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const currentData = plan.sections?.[active] || {};
      const r = await api.put("/plan", { section: active, data: currentData });
      setPlan(r.data);
      setSavedAt(new Date());
    } finally { setSaving(false); }
  };

  const completed = (key) => {
    const s = plan.sections?.[key] || {};
    return Object.values(s).some((v) => v && String(v).trim().length > 0);
  };

  const completion = Math.round((SECTIONS.filter((s) => completed(s.key)).length / SECTIONS.length) * 100);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Your private plan</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2D332D]">My Plan</h1>
          <p className="text-[#576056] mt-3 max-w-xl">A gift of clarity, {user?.name || "friend"}. Fill in what feels right — you can always come back.</p>
        </div>
        {user?.is_premium && (
          <div className="flex gap-3">
            <button data-testid="plan-export-pdf" onClick={() => exportPlanPdf(user, plan)} className="btn-secondary text-sm">
              <Download size={16} /> Export PDF
            </button>
          </div>
        )}
      </div>

      {!user?.is_premium ? (
        <PaywallCard
          title="Your Plan Builder is a Premium feature."
          subtitle="Build a private, structured plan — accounts, executors, wishes and letters — save it safely and export as PDF whenever you like. One-time £29, lifetime access."
          features={[
            "10 gentle sections, saved as you go",
            "PDF export for your executor or safe",
            "Includes AI companion + premium chapters",
            "One-time — no subscription",
          ]}
        />
      ) : (
        <>
          <PrivacyNotice variant="card" className="mb-8" />
          <PlanBody
          plan={plan}
          setPlan={setPlan}
          active={active}
          setActive={setActive}
          save={save}
          saving={saving}
          savedAt={savedAt}
          completed={completed}
          completion={completion}
        />
        </>
      )}
    </div>
  );
}

function PlanBody({ plan, setPlan, active, setActive, save, saving, savedAt, completed, completion }) {
  const currentSection = SECTIONS.find((s) => s.key === active);
  const currentData = plan.sections?.[active] || {};

  const setField = (name, val) => {
    setPlan((p) => ({
      ...p,
      sections: { ...(p.sections || {}), [active]: { ...(p.sections?.[active] || {}), [name]: val } },
    }));
  };

  return (
    <>
      {/* Progress */}
      <div className="card p-5 mb-8 flex items-center gap-5">
        <div className="text-3xl font-serif text-[#6B7F62] w-16">{completion}%</div>
        <div className="flex-1">
          <div className="h-2 rounded-full bg-[#F3F1ED] overflow-hidden">
            <div className="h-full bg-[#9EAD94] transition-all duration-500" style={{ width: `${completion}%` }} />
          </div>
          <div className="text-xs text-[#8A9488] mt-2">
            {SECTIONS.filter((s) => completed(s.key)).length} of {SECTIONS.length} sections started
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-3">Sections</div>
          <nav className="space-y-1.5" data-testid="plan-sidebar">
            {SECTIONS.map((s) => {
              const done = completed(s.key);
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  data-testid={`plan-section-${s.key}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    active === s.key ? "bg-white border border-[#9EAD94]" : "hover:bg-white border border-transparent"
                  }`}
                >
                  <div className="text-[#6B7F62]">{s.icon}</div>
                  <div className="flex-1 text-sm text-[#2D332D]">{s.title}</div>
                  {done && (
                    <div className="w-5 h-5 rounded-full bg-[#E9ECE8] flex items-center justify-center">
                      <Check size={13} className="text-[#6B7F62]" strokeWidth={2.5} />
                    </div>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Form */}
        <div className="md:col-span-8">
          <div className="card p-6 md:p-10">
            <div className="flex items-center gap-3 mb-2 text-[#6B7F62]">{currentSection.icon}
              <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488]">Section</div>
            </div>
            <h2 className="font-serif text-3xl text-[#2D332D] mb-2">{currentSection.title}</h2>
            <p className={`text-[#576056] leading-relaxed ${currentSection.sensitive ? "mb-4" : "mb-8"}`}>{currentSection.intro}</p>
            {currentSection.sensitive && (
              <div className="mb-8 inline-flex items-start gap-2 rounded-full bg-[#E9ECE8] border border-[#9EAD94]/40 px-3.5 py-1.5 text-xs text-[#2D332D]" data-testid="section-sensitive-reassurance">
                <Lock size={12} className="text-[#6B7F62] mt-[3px] flex-shrink-0" strokeWidth={2} />
                <span>Only you can read this. Even we can't. <Link to="/security" className="underline text-[#6B7F62] hover:text-[#2D332D]">How your data is kept safe</Link></span>
              </div>
            )}

            {currentSection.layout === "letters-tabs" ? (
              <LettersTabs data={currentData} setField={setField} />
            ) : (
              <div className="space-y-7">
                {currentSection.fields.map((f) => (
                  <div key={f.name}>
                    <label className={`block ${f.helper ? "font-serif text-lg text-[#2D332D] leading-snug mb-1.5" : "text-sm font-medium text-[#2D332D] mb-2"}`}>
                      {f.label}
                    </label>
                    {f.helper && (
                      <p className="text-sm text-[#576056] leading-relaxed mb-3 italic">{f.helper}</p>
                    )}
                    {f.type === "textarea" ? (
                      <textarea
                        data-testid={`plan-input-${f.name}`}
                        className="input min-h-[80px]"
                        rows={f.rows || 3}
                        value={currentData[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                      />
                    ) : (
                      <input
                        data-testid={`plan-input-${f.name}`}
                        type="text"
                        className="input"
                        value={currentData[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {currentSection.outro && (
              <div className="mt-8 p-5 rounded-xl bg-[#F5ECE7] border border-[#D1A793]/40 text-[#2D332D] text-[15px] leading-relaxed" data-testid="section-outro">
                {currentSection.outro}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-[#EAE6E1] flex items-center justify-between gap-4">
              <div className="text-xs text-[#8A9488]">
                {savedAt ? `Saved at ${savedAt.toLocaleTimeString()}` : "Changes not yet saved"}
              </div>
              <button
                data-testid="plan-save"
                onClick={save}
                disabled={saving}
                className="btn-primary text-sm"
              >
                <Save size={16} /> {saving ? "Saving…" : "Save section"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function LettersTabs({ data, setField }) {
  const [active, setActive] = useState(1);
  const LETTERS = [1, 2, 3, 4];

  const hasContent = (n) =>
    (data[`letter${n}_recipient`] || "").trim().length > 0 ||
    LETTER_PROMPTS.some((p) => (data[`letter${n}_${p.name}`] || "").trim().length > 0);

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap mb-8 pb-1" data-testid="letters-tabs">
        {LETTERS.map((n) => {
          const isActive = active === n;
          const filled = hasContent(n);
          const recipient = (data[`letter${n}_recipient`] || "").trim();
          return (
            <button
              key={n}
              onClick={() => setActive(n)}
              data-testid={`letter-tab-${n}`}
              className={`px-4 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 ${
                isActive
                  ? "bg-[#2D332D] text-white"
                  : "bg-white border border-[#EAE6E1] text-[#2D332D] hover:border-[#9EAD94]"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${filled ? "bg-[#D1A793]" : "bg-[#D4CEC4]"}`} />
              <span>Letter {n}</span>
              {recipient && !isActive && <span className="text-[#8A9488] font-normal">· {recipient}</span>}
              {recipient && isActive && <span className="text-[#B5BCB1] font-normal">· {recipient}</span>}
            </button>
          );
        })}
      </div>

      {/* Recipient */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-[#2D332D] mb-2">To</label>
        <input
          data-testid={`plan-input-letter${active}_recipient`}
          type="text"
          className="input"
          placeholder="e.g. My daughter, Emma"
          value={data[`letter${active}_recipient`] || ""}
          onChange={(e) => setField(`letter${active}_recipient`, e.target.value)}
        />
      </div>

      {/* Prompts */}
      <div className="space-y-7">
        {LETTER_PROMPTS.map((f) => {
          const key = `letter${active}_${f.name}`;
          return (
            <div key={key}>
              <label className="block font-serif text-lg text-[#2D332D] leading-snug mb-1.5">{f.label}</label>
              <p className="text-sm text-[#576056] leading-relaxed mb-3 italic">{f.helper}</p>
              <textarea
                data-testid={`plan-input-${key}`}
                className="input min-h-[80px]"
                rows={6}
                value={data[key] || ""}
                onChange={(e) => setField(key, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

